// libraries
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Button, IconButton, Skeleton, Typography, Badge, Tooltip } from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  LocalOffer as LocalOfferIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  CalculateOutlined as CalculateIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import HistoryIcon from '@mui/icons-material/History'
import { makeStyles } from '@mui/styles'
import { Stack } from '@mui/system'
import { randomId } from '@mui/x-data-grid-generator'
import Dinero from 'dinero.js'
import { useWatch } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'

// state
import { showFormModal } from 'state/ui'
import { removeScrapVehicle } from 'state/inboundTicket'

// specific components
import PaymentHoldTooltip from 'components/containers/inbound-tickets/components/PaymentHoldTooltip'
import AxleWeightsModal from 'components/containers/inbound-tickets/components/AxleWeightsModal'

// generic
import BasicDataGrid from 'components/generic/datagrid'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import CameraButton from 'components/generic/buttons/CameraButton'
import ScaleButton from 'components/generic/buttons/ScaleButton'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import AdjustmentModal from 'components/generic/modals/dialog-modal/AdjustmentModal'
import { TransparentIconLabelButton } from 'components/generic/buttons/IconLabelButton'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import ScalePopover from 'components/generic/popovers/ScalePopover'
import MultiImageBlade from 'components/generic/modals/blade-modal/MultiImageBlade'
import PurchasePriceHistoryBlade from 'components/generic/modals/blade-modal/PurchasePriceHistoryBlade'

// helpers and utils
import {
  generateAdjustmentText,
  getNetValue,
  getNetWeight,
  getTotalPriceDeduction,
  getTotalWeightDeduction,
} from 'utils/data-manipulation/common'
import {
  CHURN_ZERO_EVENTS,
  DEFAULT_SUPPLIER_TYPES,
  MEASURE_UNIT,
  MEASURE_UNIT_ARRAY,
  MODAL_VIEW_MODES,
  ROUNDING_PRECISIONS,
  DEFAULT_AXLE_DATA,
  GS_ROLE,
  IBT_ROW_TYPES,
} from 'utils/constants'
import { convertUM, dispatchSnackbar, setDecimalPrecision, formatFeeOrChargeValue } from 'utils/helpers'
import FORM_MODALS from 'utils/ui/modals'
import { useScaleState } from 'utils/custom-hooks/useScaleState'
import { useCaptureMultipleImages } from 'utils/custom-hooks/useCaptureMultipleImages'
import {
  capitalize,
  roundTo,
  formatMaterialName,
  makeCommaSeparatedString,
} from 'utils/data-manipulation/valueFormatter'
import { trackEvent } from 'utils/tracking'
import { generateRequestImagesFunction } from 'utils/images'
import {
  canEditPrice,
  getPriceErrorFn,
  priceValidationInfo,
} from 'components/containers/inbound-tickets/helpers/price-validation'
import {
  isManualValidationInfo,
  rowGridHasErrors,
} from 'components/containers/inbound-tickets/helpers/grid-validations'
import { isDifferentialOverstepped, updateDifferentialRow } from 'components/containers/inbound-tickets/utils'
import { formatAxleWeightsFromFormData } from 'components/containers/inbound-tickets/helpers/axleWeightsHelpers'
import FeeOrPromotionModal from './FeeOrPromotionModal'

const getRowClassName = params => {
  if (params.row.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE) {
    return 'scrap-vehicle-row'
  }
  return ''
}

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(1),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.differential-row': {
        background: theme.palette.error.background,
        boxShadow: 'inset 0px -1px 0px #E0E0E0',
        '&:hover': {
          cursor: 'pointer',
          background: theme.palette.error.hover,
        },
      },
      '&.scrap-vehicle-row': {
        cursor: 'pointer',
      },
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&:not(.total-row)&:not(.differential-row)': {
        '&.Mui-selected': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
        '&:not(.Mui-selected)': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell.button-cell': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .f-and-p-clickable-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}))

const ActiveMaterialList = ({
  materials,
  totalInformation,
  onUpdateMaterial,
  isUpdate,
  apiRef,
  totalApiRef,
  formApi: {
    control,
    setError,
    clearErrors,
    formState: { dirtyFields, errors, isSubmitting, isSubmitted },
    setFocus,
    getValues,
    setValue,
  },
  disabled,
  onClickDifferentialRow,
  setDifferential,
  hasEditedMaterials,
  setHasEditedMaterials,
  pricePrepend,
  onEditScrapVehicle,
  setErrorMessage,
  purchaseOrderData,
  inboundTicketData,
  saveIbt,
  handleFinishedGoodCreated,
  handleFinishedGoodDeleted,
  isTransfer,
  isDirty,
  onFeeOrPromotionUpdateSubmit,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation('inboundTicket')

  const [openDeduction, setOpenDeduction] = useState(false)
  const [selectedDeductions, setSelectedDeductions] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedIdForDeletion, setSelectedIdForDeletion] = useState(null)
  const [deductionMaterialId, setDeductionMaterialId] = useState()
  const [isDifferentialExceeded, setIsDifferentialExceeded] = useState(false)
  const [scalePopoverData, setScalePopoverData] = useState({})
  const [multiImageBladeData, setMultiImageBladeData] = useState(null)
  const { isAdmin, viewPermissions } = useSelector(state => state.permissions)
  const { customer: supplier } = useSelector(state => state.customer)
  const { supplementalPriceSheetIds } = useSelector(state => state.materials)
  const [activeRowForAxleWeights, setActiveRowForAxleWeights] = useState(null)
  const [activeFeeOrPromotionRow, setActiveFeeOrPromotionRow] = useState(null)
  const [openPurchasePriceHistoryBlade, setOpenPurchasePriceHistoryBlade] = useState(false)
  const [customerMaterialPair, setCustomerMaterialPair] = useState({})

  const effectiveDate = useWatch({ control, name: 'effectiveDate' })

  const {
    showWeightDecimals,
    decimalPrecision: decimalPrecisionSetting = 2,
    thresholdAmount,
    thresholdUM,
    flagManuallyEnteredWeights,
    disableUmDropdowns,
    enableAxleWeights,
    defaultNumberOfAxles,
  } = useSelector(state => state.settings?.settings?.purchaseSettings?.inboundTickets || {})

  const { transportationInformation } = useSelector(state => state.inboundTicket)
  const purchaseOrderId = transportationInformation?.purchaseOrderId || ''
  const priceWritePermission = isAdmin || viewPermissions?.inboundTicket?.priceWrite

  const { requestImages: requestImagesLegacy } = generateRequestImagesFunction()

  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()

  const { roles } = useSelector(state => state.permissions)
  const roleAllowsMaterialWeightEdit = !!roles?.find(
    r =>
      r.roleName === GS_ROLE.YARD_ADMIN || r.roleName === GS_ROLE.SCALE_OPERATOR || r.roleName === GS_ROLE.YARD_MANAGER,
  )
  const {
    canAutoSelect,
    canFlagManualScaleReadOverrideFF,
    captureWeightsIBTFeatureFlagOn,
    canUpdateUm,
    show20MaterialsOnIBTs,
    enableMaterialPaymentHolds,
    enableRequestImagesV2,
    removeZerosFF,
    enablePullApartFeatures,
    enableTransferTicketsConsumerWeightsFF,
  } = useSelector(state => state.featureFlags?.variables || {})
  const canFlagManualScaleReadOverride = canFlagManualScaleReadOverrideFF && flagManuallyEnteredWeights

  const { selectedCameraId, selectedScaleIds, selectedCameraIds, scaleCameras } = useSelector(state => state.devices)

  const { requestImages, errorRequestingImages } = useCaptureMultipleImages()

  const handleImageCaptureError = error => {
    dispatchSnackbar(dispatch, 'error', `Error capturing image: ${error.message || 'Unknown error'}. Please try again.`)
  }

  useEffect(() => {
    if (enableRequestImagesV2 && errorRequestingImages) {
      handleImageCaptureError(errorRequestingImages)
    }
  }, [errorRequestingImages])

  const callReadScale = ({ material, weightType, scaleId = '' }) => {
    readScale(scaleData => {
      const isTruck = material.id === 'truck-total'
      const ref = isTruck ? totalApiRef : apiRef

      if (canFlagManualScaleReadOverride) {
        ref.current.updateRows([
          {
            ...material,
            [weightType]: scaleData?.weight,
            scaleButtonClicked: true,
            axleWeights: [],
          },
        ])
      } else {
        ref.current.updateRows([{ ...material, [weightType]: scaleData?.weight, axleWeights: [] }])
      }
    }, scaleId)
  }

  const scaleButtonClickHandler = (material, weightType, event) => {
    if (!['gross', 'tare'].includes(weightType)) throw new Error('Invalid weight type!')

    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
        material,
        weightType,
      })
    } else {
      callReadScale({ material, weightType })

      // Create array of filenames with deviceIds for each camera
      const fileNamesArray =
        selectedScaleIds[0] && scaleCameras[selectedScaleIds[0]]?.length
          ? scaleCameras[selectedScaleIds[0]].map(cameraId => ({
              imageFilename: `${uuidv4()}-${weightType}.jpg`,
              deviceId: cameraId,
            }))
          : selectedCameraIds.map(cameraId => ({
              imageFilename: `${uuidv4()}-${weightType}.jpg`,
              deviceId: cameraId,
            }))

      // Request images with the generated filenames
      requestImages(
        imageData => {
          if (imageData.length > 0) {
            const fieldName = `materialGrid.${material.id}.${weightType}Images`
            setValue(fieldName, imageData)
            const ref = material.id === 'truck-total' ? totalApiRef : apiRef
            ref.current.updateRows([
              {
                id: material.id,
                [`${weightType}Images`]: imageData,
              },
            ])
          }
        },
        fileNamesArray,
        selectedScaleIds[0],
      )
    }
  }

  useEffect(() => {
    let isMounted = true
    if (isUpdate && isSubmitted && rowGridHasErrors(errors, null, 'tare') && isMounted) {
      setErrorMessage('Tare weight is required for some materials.')
    }
    return () => {
      isMounted = false
    }
  }, [isSubmitted, isSubmitting, errors, setErrorMessage, isUpdate])

  useEffect(() => {
    if (totalApiRef?.current?.subscribeEvent) {
      totalApiRef.current.subscribeEvent('rowsUpdate', () => {
        const isOrNor = isDifferentialOverstepped(totalApiRef, thresholdAmount, thresholdUM)
        setIsDifferentialExceeded(isOrNor)
      })
    }
  }, [totalApiRef])

  const { defaultYardUm } = useSelector(state => state.settings)

  const handleDeleteClick = id => {
    const materialRow = apiRef.current.getRow(id)
    const totalsRow = totalApiRef.current.getRow(id)
    // we mutate two different grids so we have to distinguish between them
    const ref = materialRow ? apiRef : totalsRow ? totalApiRef : null
    if (!ref) return

    const { rowType } = ref.current.getRow(id)

    if (rowType === IBT_ROW_TYPES.SCRAP_VEHICLE) {
      dispatch(removeScrapVehicle(id))
    }
    if (rowType === IBT_ROW_TYPES.FEE || rowType === IBT_ROW_TYPES.PROMOTION) {
      const current = getValues('feeOrPromotion')
      const updated = { ...current }
      delete updated[id]
      setValue('feeOrPromotion', updated)
    }

    ref.current.updateRows([{ id, _action: 'delete' }])
    const rowIds = ref.current.getAllRowIds()
    ref.current.updateRows(rowIds.map((rowId, index) => ({ id: rowId, rowIndex: index + 1 })))
    clearErrors(id)
    onUpdateMaterial()
    setShowDeleteModal(false)
  }

  const handleChange = (event, row) => {
    const cell = event?.target?.name?.split('.').pop()
    const newValue = event?.target?.value
    const { gross, tare, um, deductions, price, customerPreferredUm } = row
    const updatedRow = { ...row }

    if (cell !== 'price') {
      const value = getNetValue({
        gross: Number(gross) || 0,
        tare: Number(tare) || 0,
        price: Number(price) || 0,
        [cell]: Number(newValue) || 0,
        deductions,
        fromUnit: defaultYardUm,
        toUnit: um,
      })
      updatedRow[cell] = newValue
      updatedRow.netValue = roundTo(value, ROUNDING_PRECISIONS.COST)
    } else {
      const newBasePrice = roundTo(convertUM(newValue, um, customerPreferredUm), ROUNDING_PRECISIONS.PRICE)
      const value = getNetValue({
        gross: Number(gross) || 0,
        tare: Number(tare) || 0,
        price: Number(newBasePrice) || 0,
        deductions,
        fromUnit: defaultYardUm,
        toUnit: um,
      })
      updatedRow.price = Number(newBasePrice) || 0
      updatedRow.customerPreferredPrice = roundTo(newValue, ROUNDING_PRECISIONS.PRICE)
      updatedRow.netValue = roundTo(value, ROUNDING_PRECISIONS.COST)
    }

    const ref = row.rowType === IBT_ROW_TYPES.TRUCK_TOTAL ? totalApiRef : apiRef

    const isWeightCell = !!['gross', 'tare'].includes(cell)
    if (canFlagManualScaleReadOverride && isWeightCell) {
      const current = ref.current.getRow(row.id)
      if (event.source === 'event') {
        // source is 'event' when field is manually edited
        updatedRow[`isManual${capitalize(cell)}`] = true
      } else if (event.source === 'prop' && current.scaleButtonClicked) {
        // source is 'prop' when scale button is clicked OR axle weight modal is used
        updatedRow[`isManual${capitalize(cell)}`] = false
        updatedRow.scaleButtonClicked = false
      }
    }

    ref.current.updateRows([updatedRow])
  }

  const renderPaymentHoldTooltip = useCallback(
    rules => {
      const initialDate = effectiveDate
      return enableMaterialPaymentHolds &&
        supplier &&
        supplier?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
        initialDate ? (
        <PaymentHoldTooltip rulesArray={rules} effectiveDateValue={new Date(initialDate).toISOString()} />
      ) : null
    },
    [supplier, effectiveDate, enableMaterialPaymentHolds],
  )

  const materialColumns = useMemo(() => {
    const columns = [
      {
        field: 'number',
        headerName: '#',
        flex: 0.5,
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row: { rowIndex } }) => <Typography>{rowIndex}</Typography>,
        sortable: true,
      },
      {
        field: 'code',
        headerName: 'Material',
        minWidth: 175,
        flex: 2.5,
        sortable: false,
        renderCell: params => {
          const { row } = params
          if (row.rowType === IBT_ROW_TYPES.DIFFERENTIAL) {
            return (
              <>
                {t('differential')}
                {isDifferentialExceeded ? <ErrorIcon fontSize="small" sx={{ ml: 1 }} color="error" /> : null}
              </>
            )
          }
          if (row.rowType === IBT_ROW_TYPES.TRUCK_TOTAL) {
            return t('truck_total')
          }
          if (row.rowType === IBT_ROW_TYPES.FEE || row.rowType === IBT_ROW_TYPES.PROMOTION) {
            return (
              <>
                {row.code}
                {row.showPaymentMethodSpecificIcon && (
                  <Tooltip title={`Only applies to ${makeCommaSeparatedString(row.eligiblePaymentTypes)} payments`}>
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
                  </Tooltip>
                )}
              </>
            )
          }
          if (row.rowType === IBT_ROW_TYPES.LOAD_TOTAL || row.rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK) {
            return row.code
          }

          if (row.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE) {
            return (
              <Stack>
                <Typography>{row.code}</Typography>
                <Typography variant="caption" color="text.hint">
                  {row.vin}
                </Typography>
              </Stack>
            )
          }

          if (disabled) {
            const renderIcon =
              enableMaterialPaymentHolds &&
              supplier &&
              supplier?.supplierType === DEFAULT_SUPPLIER_TYPES.RETAIL &&
              effectiveDate
            const purchaseOrderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(
              entry => entry.id === row.orderEntryId,
            )
            return row.materialType ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={
                    renderIcon && {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  }
                >
                  {formatMaterialName({
                    commonName: row.materialType?.commonName,
                    code: row.materialType.code,
                    overrideName: purchaseOrderEntry?.overrideName,
                  })}
                </Typography>
                <>{enableMaterialPaymentHolds && renderPaymentHoldTooltip(row.materialType?.rules)}</>
              </Stack>
            ) : (
              ''
            )
          }
          return (
            <>
              <MaterialAutocomplete
                variant="outlined"
                size="small"
                defaultValue={{
                  commonName: row.materialType?.commonName,
                  code: row.materialType?.code,
                  materialTypeId: row.materialTypeId,
                  orderEntryId: row.orderEntryId,
                }}
                onUpdateMaterial={onUpdateMaterial}
                canAutoSelect={canAutoSelect}
                useSupplementalPriceSheetIds
                canOverridePrices={!!Object.keys(dirtyFields).length}
                onChange={(value, reason) => {
                  if (!value) return
                  const isFirstLoad = reason === undefined
                  const {
                    gross,
                    tare,
                    deductions,
                    id,
                    materialType,
                    price,
                    customerPreferredUm,
                    customerPreferredPrice,
                    um,
                  } = row
                  const {
                    code,
                    id: materialTypeId,
                    orderEntryId,
                    commonName,
                    defaultPriceEntry,
                    supplementalPriceEntry,
                    tareWeightRequired,
                    rules,
                  } = value
                  if (reason === 'load price entry') {
                    apiRef.current.updateRows([
                      {
                        ...row,
                        defaultPriceEntry,
                        overridePriceEntry: supplementalPriceEntry,
                      },
                    ])
                    return
                  }
                  const priceEntry = supplementalPriceEntry || defaultPriceEntry
                  const defaultPrice =
                    priceEntry && typeof priceEntry?.buyPrice?.price === 'object'
                      ? Dinero({ ...priceEntry?.buyPrice?.price }).toUnit()
                      : Number(priceEntry?.buyPrice?.price) || 0
                  const materialPrice = isFirstLoad && (price || price === 0) ? price : defaultPrice
                  const preferredPrice =
                    isFirstLoad && (customerPreferredPrice || customerPreferredPrice === 0)
                      ? customerPreferredPrice
                      : materialPrice
                  const newUm = isFirstLoad && um ? um : priceEntry?.weightUnits || defaultYardUm
                  const preferredUm = isFirstLoad && customerPreferredUm ? customerPreferredUm : newUm
                  const isTareWeightRequired = isFirstLoad
                    ? materialType?.tareWeightRequired || false
                    : tareWeightRequired
                  const newNetValue = getNetValue({
                    gross: Number(gross) || 0,
                    tare: Number(tare) || 0,
                    price: Number(materialPrice) || 0,
                    deductions,
                    fromUnit: defaultYardUm,
                    toUnit: newUm,
                  })
                  const newRow = {
                    ...row,
                    code,
                    materialTypeId,
                    orderEntryId,
                    materialName: commonName || '',
                    um: newUm,
                    customerPreferredUm: preferredUm,
                    price: setDecimalPrecision(materialPrice, {
                      decimalPrecisionOverride: 4,
                      shouldFormatNumber: false,
                    }),
                    customerPreferredPrice: setDecimalPrecision(preferredPrice, {
                      decimalPrecisionOverride: 4,
                      shouldFormatNumber: false,
                    }),
                    netValue: newNetValue,
                    tareWeightRequired: isTareWeightRequired,
                    defaultPriceEntry,
                    overridePriceEntry: supplementalPriceEntry,
                    materialTypeRules: rules || [],
                  }
                  apiRef.current.updateRows([newRow])
                  setValue(`materialGrid.${id}.tareWeightRequired`, isTareWeightRequired)
                  if (isTareWeightRequired) {
                    setValue(`materialGrid.${id}.tare`, tare)
                  } else if (errors?.materialGrid?.[id]?.tare) {
                    clearErrors(`materialGrid.${id}.tare`)
                  }
                }}
                orderData={purchaseOrderId && purchaseOrderId === purchaseOrderData?.id ? purchaseOrderData : null}
              />
              {renderPaymentHoldTooltip(row.materialTypeRules)}
            </>
          )
        },
      },
      {
        field: 'axle-weight-button',
        headerName: '',
        sortable: false,
        width: 50,
        align: 'right',
        cellClassName: 'button-cell',
        renderCell: params => {
          const { row } = params
          const { rowType, axleWeights = [] } = row

          if (
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }

          return (
            <IconButton size="small" style={{ padding: '4px' }} onClick={() => setActiveRowForAxleWeights(row)}>
              <Badge color="primary" variant="dot" invisible={axleWeights?.length === 0}>
                <CalculateIcon />
              </Badge>
            </IconButton>
          )
        },
      },
      {
        field: 'gross',
        headerName: `Gross (${defaultYardUm})`,
        minWidth: 115,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { gross, rowType, id, isManualGross } = row
          if (
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }
          if (disabled || rowType === IBT_ROW_TYPES.LOAD_TOTAL) {
            return `${setDecimalPrecision(gross, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}`
          }
          const ref = id === 'truck-total' ? totalApiRef : apiRef
          const { color, startAdornment } = isManualValidationInfo(isManualGross, canFlagManualScaleReadOverride)
          return (
            <EditableCell
              control={control}
              name={`materialGrid.${id}.gross`}
              defaultValue={gross || gross === 0 ? String(gross) : ''}
              isEdit
              size="small"
              removeTrailingZeros
              color={color}
              disabled={!roleAllowsMaterialWeightEdit}
              focused={canFlagManualScaleReadOverride && isManualGross}
              {...(removeZerosFF && {
                onFocus: event => {
                  if (event.target.value === '0') {
                    setValue(`materialGrid.${id}.gross`, '')
                    ref.current.updateRows([{ id, gross: '' }])
                  }
                  setTimeout(() => event.target.focus(), 100)
                },
              })}
              {...(removeZerosFF && {
                onBlur: event => {
                  if (event.target.value === '') {
                    setValue(`materialGrid.${id}.gross`, '0')
                    ref.current.updateRows([{ id, gross: '0' }])
                  }
                },
              })}
              InputProps={{
                inputComponent: InputFormat,
                startAdornment,
                inputProps: {
                  name: 'gross',
                  inputType: 'number',
                  decimalScale: showWeightDecimals ? 1 : 0,
                  onChange: event => {
                    let rowUpdated = row
                    // request images if there are selected camera ids and no gross images
                    if (
                      Number(event.target.value) &&
                      selectedCameraIds?.length > 0 &&
                      (row.grossImages || []).length === 0
                    ) {
                      if (enableRequestImagesV2) {
                        // v2
                        const fileNamesArray = selectedCameraIds.map(cameraId => ({
                          imageFilename: `${uuidv4()}-gross.jpg`,
                          deviceId: cameraId,
                        }))

                        requestImages(imageData => {
                          if (imageData.length === 0) {
                            handleImageCaptureError(new Error('No images captured'))
                            return
                          }
                          const imagesJoined = imageData.map(image => image.imageFilename).join(', ')
                          const msg = `${imageData.length} gross images captured: ${imagesJoined}`
                          trackEvent({
                            eventName: CHURN_ZERO_EVENTS.CAPTURE_IMAGE,
                            description: msg,
                          })
                        }, fileNamesArray)
                        rowUpdated = {
                          ...row,
                          grossImages: fileNamesArray,
                        }
                        setValue(`materialGrid.${id}.grossImages`, fileNamesArray)
                        setHasEditedMaterials(true)
                      } else {
                        // legacy
                        requestImagesLegacy(imageData => {
                          if (!imageData || imageData.length === 0) {
                            handleImageCaptureError(new Error('No images captured'))
                            return
                          }
                          ref.current.updateRows([
                            {
                              id,
                              grossImages: imageData,
                            },
                          ])
                          setValue(`materialGrid.${id}.grossImages`, imageData)
                          setHasEditedMaterials(true)
                        })
                      }
                    }
                    handleChange(event, rowUpdated)
                    if (event.source === 'event') {
                      ref.current.updateRows([{ id, axleWeights: [] }])
                    }
                    if (id === 'truck-total') {
                      const truckTotal = Number(event.target.value) - Number(row.tare)
                      const loadTotal = totalApiRef.current?.getRow('load-total')?.netWeight || 0
                      const differential = truckTotal - loadTotal
                      updateDifferentialRow(totalApiRef, truckTotal, loadTotal, getValues('freightCost.chargeBack'))
                      setDifferential(differential)
                    }
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'iot-buttons-gross',
        headerName: '',
        sortable: false,
        width: 64,
        cellClassName: 'button-cell',
        renderCell: params => {
          const { row } = params
          const { rowType, grossImages, axleWeights } = row

          if (
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }

          const scaleButton =
            captureWeightsIBTFeatureFlagOn && showScaleButton ? (
              <ScaleButton
                disabled={disabled || isFetchingScaleReading}
                onClick={event => scaleButtonClickHandler(row, 'gross', event)}
              />
            ) : null

          const cameraButton =
            rowType !== IBT_ROW_TYPES.SCRAP_VEHICLE ? (
              <CameraButton
                showBadge={!!grossImages?.length}
                handleCameraClick={() => {
                  const newMultiImageBladeData = { ...row }
                  if (axleWeights?.length > 1) {
                    newMultiImageBladeData.axleNumber = 1
                  }
                  setMultiImageBladeData(newMultiImageBladeData)
                }}
                disabled={selectedCameraIds?.length > 0 && !grossImages?.length && !(axleWeights?.length > 1)}
              />
            ) : null

          return (
            <>
              {scaleButton}
              {cameraButton}
            </>
          )
        },
      },
      {
        field: 'tare',
        headerName: `Tare (${defaultYardUm})`,
        flex: 1,
        minWidth: 115,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { id, tare, rowType, isManualTare } = row
          if (
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }
          if (disabled || rowType === IBT_ROW_TYPES.LOAD_TOTAL) {
            return `${setDecimalPrecision(tare, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}`
          }
          const ref = id === 'truck-total' ? totalApiRef : apiRef
          const { color, startAdornment } = isManualValidationInfo(isManualTare, canFlagManualScaleReadOverride)

          return (
            <EditableCell
              control={control}
              name={`materialGrid.${id}.tare`}
              defaultValue={tare || tare === 0 ? String(tare) : ''}
              isEdit
              size="small"
              removeTrailingZeros
              showErrorsBeforeSubmit
              getError={() => rowGridHasErrors(errors, id, 'tare')}
              color={rowGridHasErrors(errors, id, 'tare') ? 'error' : color}
              disabled={!roleAllowsMaterialWeightEdit}
              focused={rowGridHasErrors(errors, id, 'tare') || (canFlagManualScaleReadOverride && isManualTare)}
              {...(removeZerosFF && {
                onFocus: event => {
                  if (event.target.value === '0') {
                    setValue(`materialGrid.${id}.tare`, '')
                    ref.current.updateRows([{ id, tare: '' }])
                  }
                  setTimeout(() => event.target.focus(), 100)
                },
              })}
              {...(removeZerosFF && {
                onBlur: event => {
                  if (event.target.value === '') {
                    setValue(`materialGrid.${id}.tare`, '0')
                    ref.current.updateRows([{ id, tare: '0' }])
                  }
                },
              })}
              InputProps={{
                inputComponent: InputFormat,
                startAdornment,
                inputProps: {
                  name: 'tare',
                  inputType: 'number',
                  decimalScale: showWeightDecimals ? 1 : 0,
                  onChange: event => {
                    let rowUpdated = row
                    // request images if there are selected camera ids and no tare images
                    if (
                      Number(event.target.value) &&
                      selectedCameraIds?.length > 0 &&
                      (row.tareImages || []).length === 0
                    ) {
                      if (enableRequestImagesV2) {
                        // v2
                        const fileNamesArray = selectedCameraIds.map(cameraId => ({
                          imageFilename: `${uuidv4()}-tare.jpg`,
                          deviceId: cameraId,
                        }))

                        requestImages(imageData => {
                          if (imageData.length === 0) return
                          const imagesJoined = imageData.map(image => image.imageFilename).join(', ')
                          const msg = `${imageData.length} tare images captured: ${imagesJoined}`
                          trackEvent({
                            eventName: CHURN_ZERO_EVENTS.CAPTURE_IMAGE,
                            description: msg,
                          })
                        }, fileNamesArray)

                        rowUpdated = {
                          ...row,
                          tareImages: fileNamesArray,
                        }
                        setValue(`materialGrid.${id}.tareImages`, fileNamesArray)
                        setHasEditedMaterials(true)
                      } else {
                        // legacy
                        requestImagesLegacy(imageData => {
                          ref.current.updateRows([
                            {
                              id,
                              tareImages: imageData,
                            },
                          ])
                          setValue(`materialGrid.${id}.tareImages`, imageData)
                          setHasEditedMaterials(true)
                        })
                      }
                    }
                    handleChange(event, rowUpdated)
                    if (event.target?.value) {
                      clearErrors(`materialGrid.${id}.tare`)
                    }
                    if (isUpdate) {
                      setValue(`materialGrid.${id}.tareWeightRequired`, row.tareWeightRequired)
                    }
                    if (event.source === 'event') {
                      ref.current.updateRows([{ id, axleWeights: [] }])
                    }
                    if (id === 'truck-total') {
                      const truckTotal = Number(row.gross) - Number(event.target.value)
                      const loadTotal = totalApiRef.current?.getRow('load-total')?.netWeight || 0
                      const differential = truckTotal - loadTotal
                      updateDifferentialRow(totalApiRef, truckTotal, loadTotal, getValues('freightCost.chargeBack'))
                      setDifferential(differential)
                    }
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'iot-buttons-tare',
        headerName: '',
        sortable: false,
        width: 64,
        cellClassName: 'button-cell',
        renderCell: params => {
          const { row } = params
          const { rowType, tareImages, axleWeights } = row

          if (
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }

          const scaleButton =
            captureWeightsIBTFeatureFlagOn && showScaleButton ? (
              <ScaleButton
                disabled={disabled || isFetchingScaleReading}
                onClick={event => scaleButtonClickHandler(row, 'tare', event)}
              />
            ) : null

          const cameraButton =
            rowType !== IBT_ROW_TYPES.SCRAP_VEHICLE ? (
              <CameraButton
                showBadge={!!tareImages?.length}
                handleCameraClick={() => {
                  const newMultiImageBladeData = { ...row }
                  if (axleWeights?.length > 1) {
                    newMultiImageBladeData.axleNumber = 1
                  }
                  setMultiImageBladeData(newMultiImageBladeData)
                }}
                disabled={selectedCameraIds?.length > 0 && !tareImages?.length && !(axleWeights?.length > 1)}
              />
            ) : null

          return (
            <>
              {scaleButton}
              {cameraButton}
            </>
          )
        },
      },
      {
        field: 'deductions',
        headerName: 'Adjustments',
        minWidth: 80,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { gross = 0, tare = 0, price = 0, deductions = [], totalDeductions, id, rowType, um } = params.row
          if (rowType === IBT_ROW_TYPES.LOAD_TOTAL) {
            return totalDeductions?.[0] === '$' ? `${pricePrepend}${totalDeductions.slice(1)}` : totalDeductions
          }
          if (
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }

          const openDeductionModal = () => {
            setOpenDeduction(true)
            setDeductionMaterialId(id)

            setSelectedDeductions(apiRef.current.getRow(id).deductions)
          }

          const totalWeightDeduction = getTotalWeightDeduction(deductions, gross - tare)
          const totalPriceDeduction = getTotalPriceDeduction(
            deductions,
            convertUM((gross - tare) * price, defaultYardUm, um),
          )
          const deductionText = generateAdjustmentText(totalWeightDeduction, totalPriceDeduction, pricePrepend)
          if (disabled) return deductionText

          return (
            <Box>
              <TransparentIconLabelButton
                endIcon={deductions.length ? <RemoveRedEyeIcon /> : null}
                text={deductions.length ? deductionText : 'Add'}
                variant="text"
                fullWidth
                onClick={openDeductionModal}
                sx={{
                  justifyContent: 'flex-end',
                }}
              />
            </Box>
          )
        },
        sortable: false,
      },
      {
        field: 'netWeight',
        headerName: `Net (${defaultYardUm})`,
        minWidth: 115,
        flex: 1,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { netWeight, rowType } = params.row
          if (
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }
          return `${setDecimalPrecision(
            rowType === IBT_ROW_TYPES.LOAD_TOTAL || rowType === IBT_ROW_TYPES.DIFFERENTIAL
              ? netWeight
              : getNetWeight(params.row),
            {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            },
          )}`
        },
        sortable: false,
      },
      {
        field: 'price',
        headerName: 'Price/UM',
        flex: 1,
        minWidth: 130,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: ({ row = {} }) => {
          const {
            price = 0,
            rowType,
            id,
            materialTypeId,
            customerPreferredUm,
            customerPreferredPrice,
            defaultPriceEntry,
            overridePriceEntry,
          } = row
          const materialPrice = customerPreferredPrice || customerPreferredPrice === 0 ? customerPreferredPrice : price
          if (
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION ||
            !materialTypeId
          ) {
            return ''
          }

          if (!disabled && !overridePriceEntry && !defaultPriceEntry && !isTransfer) {
            return (
              <Skeleton animation="wave" width="75%" height="1rem">
                <Typography variant="body2" />
              </Skeleton>
            )
          }

          const { color, startAdornment, priceEntryPrice, minMaxRange } = priceValidationInfo({
            materialPrice,
            priceEntry: overridePriceEntry || defaultPriceEntry,
            isAdmin,
            priceType: 'buyPrice',
            customerPreferredUm,
            isTransfer: enableTransferTicketsConsumerWeightsFF ? isTransfer : false,
          })
          if (
            !canEditPrice({
              id,
              dirtyFields,
              disabled,
              priceWritePermission,
              minMaxRange,
              priceEntryPrice,
              isAdmin,
              materialPrice,
            })
          ) {
            return `${pricePrepend}${setDecimalPrecision(materialPrice)}`
          }
          const inputName = `materialGrid.${id}.price`
          const getError = getPriceErrorFn({
            isAdmin,
            priceEntryPrice,
            minMaxRange,
          })

          return (
            <EditableCell
              control={control}
              name={inputName}
              defaultValue={materialPrice || materialPrice === 0 ? setDecimalPrecision(materialPrice) : ''}
              isEdit
              size="small"
              getError={getError}
              showErrorsBeforeSubmit
              color={color}
              focused={!!color}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  pricePrepend,
                  skipSourceInfoCheck: false,
                  decimalScale: decimalPrecisionSetting,
                  onChange: event => {
                    if (event.target.value.toString() === '.') return

                    handleChange(event, row)

                    const error = getError(event.target.value)
                    if (error) {
                      setError(inputName, {
                        type: 'minMaxRangeError',
                        message: 'Material price is outside min/max boundaries.',
                      })
                    } else {
                      clearErrors(inputName)
                    }
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
                startAdornment,
              }}
            />
          )
        },
      },
      {
        field: 'um',
        headerName: 'UM',
        width: 100,
        sortable: false,
        renderCell: ({ row }) => {
          const { rowType, price, um, customerPreferredUm } = row
          if (
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION ||
            !row?.materialTypeId
          ) {
            return ''
          }
          if (!canUpdateUm || um?.toLowerCase() === MEASURE_UNIT.EACH || disabled)
            return (customerPreferredUm || um)?.toUpperCase()

          const handleUMChange = ({ target: { value } }) => {
            // because we are converting price, units need to be flipped
            const updatedCustomerPreferredPrice = convertUM(price, value.toLowerCase(), um)
            const updatedRow = {
              customerPreferredUm: value.toLowerCase(),
              customerPreferredPrice: roundTo(updatedCustomerPreferredPrice, decimalPrecisionSetting),
            }

            trackEvent({
              eventName: CHURN_ZERO_EVENTS.CHANGE_UM,
              description: `${um} => ${value.toLowerCase()}`,
            })

            apiRef.current.updateRows([{ ...row, ...updatedRow }])
          }

          return (
            <EditableCell
              name={`materialGrid.${row.id}.um`}
              label=""
              control={control}
              defaultValue={(customerPreferredUm || um)?.toLowerCase()}
              cellType="SELECT"
              size="small"
              options={MEASURE_UNIT_ARRAY.filter(({ id }) => id !== 'ea')}
              isEdit
              InputProps={{
                sx: { fontSize: '0.875rem' },
                inputProps: { onChange: handleUMChange },
              }}
            />
          )
        },
      },
      {
        field: 'history',
        headerName: '',
        flex: 0.25,
        renderCell: params => {
          const { row } = params
          const { rowType, materialTypeId } = row

          const hideHistoryButton = !supplier.id || !materialTypeId

          if (
            hideHistoryButton ||
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }

          return (
            <IconButton
              onClick={() => {
                setCustomerMaterialPair({ materialTypeId, customerId: supplier.id })
                setOpenPurchasePriceHistoryBlade(true)
              }}
            >
              <HistoryIcon />
            </IconButton>
          )
        },
      },
      {
        field: 'netValue',
        headerName: 'Value',
        flex: 1,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        minWidth: 115,
        renderCell: params => {
          const { netValue = 0, overridePriceEntry, defaultPriceEntry, rowType } = params.row
          if (
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL
          ) {
            return ''
          }

          // Only use special formatting for fees and freight charges (with parentheses)
          const isNegativeType = rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK || rowType === IBT_ROW_TYPES.FEE
          if (isNegativeType) {
            return formatFeeOrChargeValue(netValue, pricePrepend, true, 2)
          }

          // For materials and load totals, use standard formatting (show negative as -$1.00)
          return `${pricePrepend}${setDecimalPrecision(netValue || 0, { decimalPrecisionOverride: 2 })}`
        },
        sortable: false,
      },
      {
        field: 'finishedGoodTagNumber',
        headerName: 'FG Tag #',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
      },
      {
        field: 'finishedGoodItemCount',
        headerName: 'FG Count',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { row = {} } = params
          const { finishedGood, finishedGoodItemCount, rowType, id } = row
          if (
            !finishedGood?.id ||
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.SCRAP_VEHICLE ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK ||
            rowType === IBT_ROW_TYPES.FEE ||
            rowType === IBT_ROW_TYPES.PROMOTION
          ) {
            return ''
          }
          if (disabled) {
            return finishedGoodItemCount
          }
          return (
            <EditableCell
              control={control}
              name={`materialGrid.${id}.finishedGoodItemCount`}
              defaultValue={finishedGoodItemCount || finishedGoodItemCount === 0 ? String(finishedGoodItemCount) : ''}
              isEdit
              size="small"
              removeTrailingZeros
              disabled={!roleAllowsMaterialWeightEdit}
              {...(removeZerosFF && {
                onFocus: event => {
                  if (event.target.value === '0') {
                    setValue(`materialGrid.${id}.finishedGoodItemCount`, '')
                    apiRef.current.updateRows([{ id, finishedGoodItemCount: '' }])
                  }
                  setTimeout(() => event.target.focus(), 100)
                },
              })}
              {...(removeZerosFF && {
                onBlur: event => {
                  if (event.target.value === '') {
                    setValue(`materialGrid.${id}.finishedGoodItemCount`, '0')
                    apiRef.current.updateRows([{ id, finishedGoodItemCount: '0' }])
                  }
                },
              })}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'finishedGoodItemCount',
                  inputType: 'number',
                  decimalScale: 0,
                  allowNegative: false,
                  onChange: event => {
                    handleChange(event, row)
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'actions',
        minWidth: 100,
        flex: 1,
        headerName: '',
        renderCell: params => {
          const { row = {} } = params
          const { id, rowType, gross, tare, materialType, materialTypeId, finishedGood, finishedGoodItemCount } = row
          const parseGross = parseInt(gross, 10) || 0
          const parseTare = parseInt(tare, 10) || 0
          if (
            disabled ||
            rowType === IBT_ROW_TYPES.LOAD_TOTAL ||
            rowType === IBT_ROW_TYPES.TRUCK_TOTAL ||
            rowType === IBT_ROW_TYPES.DIFFERENTIAL ||
            rowType === IBT_ROW_TYPES.FREIGHT_CHARGEBACK
          ) {
            return ''
          }
          const isFeeOrPromotionRow = [IBT_ROW_TYPES.PROMOTION, IBT_ROW_TYPES.FEE].includes(rowType)
          return (
            <div>
              <IconButton
                color="error"
                size="small"
                sx={{
                  fill: 'red',
                  width: '40px',
                  height: '40px',
                }}
                aria-label="delete"
                onClick={event => {
                  event.stopPropagation()
                  if (isFeeOrPromotionRow) {
                    handleDeleteClick(id)
                  } else {
                    setSelectedIdForDeletion(id)
                    setShowDeleteModal(true)
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>

              {!isFeeOrPromotionRow && (
                <IconButton
                  size="small"
                  aria-label="Create Finished Good From Material"
                  sx={{
                    width: '40px',
                    height: '40px',
                  }}
                  // For now, disable the "create FG" button if the IBT has not been created yet. Otherwise, inboundTicketData doesn't get passed into the FG blade properly.
                  // In the future, maybe we can look into adding the IBT id to redux and then making a get-ibt-by-id call from the FG blade
                  disabled={!isUpdate}
                  onClick={() => {
                    const pageWasEdited = isDirty || hasEditedMaterials
                    // Save pending changes before creating a finished good
                    if (pageWasEdited && saveIbt) {
                      saveIbt()
                    }
                    let viewMode = finishedGood?.id ? MODAL_VIEW_MODES.EDIT : MODAL_VIEW_MODES.NEW

                    // If this is a transfer ticket, users can only see the finished good, but not edit it
                    if (finishedGood?.id && inboundTicketData.transferredFromObtId) {
                      viewMode = MODAL_VIEW_MODES.VIEW
                    }

                    dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.NEW_FINISHED_GOOD,
                        formModalProps: {
                          viewMode: viewMode,
                          handleFinishedGoodCreated: handleFinishedGoodCreated,
                          handleFinishedGoodDeleted,
                          isUpdate: false,
                          finishedGoodProps: {
                            rowData: row,
                            materialTypeId,
                            materialName: materialType?.commonName,
                            materialCode: materialType?.code,
                            gross: parseGross,
                            tare: parseTare,
                            net: parseGross - parseTare,
                            inboundTicketData: inboundTicketData,
                            itemCount: finishedGoodItemCount,
                          },
                        },
                      }),
                    )
                  }}
                >
                  <LocalOfferIcon fontSize="small" color={finishedGood?.id ? 'success' : 'inherit'} />
                </IconButton>
              )}
            </div>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        width: 30,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (disableUmDropdowns && column.field === 'um') return false
      if (!enableAxleWeights && column.field === 'axle-weight-button') return false
      if (!isTransfer && (column.field === 'finishedGoodTagNumber' || column.field === 'finishedGoodItemCount'))
        return false
      if (!enablePullApartFeatures && column.field === 'finishedGoodItemCount') return false
      return true
    })
    return filteredColumns
  }, [
    deductionMaterialId,
    materials,
    isAdmin,
    dirtyFields,
    showScaleButton,
    isFetchingScaleReading,
    selectedCameraId,
    selectedCameraIds,
    selectedScaleIds,
    apiRef,
    totalApiRef,
    isDifferentialExceeded,
    canUpdateUm,
    pricePrepend,
    errors?.materialGrid,
    disabled,
    effectiveDate,
    supplier,
    supplementalPriceSheetIds,
    disableUmDropdowns,
    enableAxleWeights,
    removeZerosFF,
    isTransfer,
    isDirty,
    hasEditedMaterials,
    defaultYardUm,
  ])

  const onClickNewMaterial = useCallback(() => {
    const id = randomId()
    const updatedRow = {
      id,
      rowType: IBT_ROW_TYPES.MATERIAL,
      isNew: true,
      rowIndex: apiRef.current.getRowsCount() + 1,
      gross: 0,
      tare: null,
    }
    if (enableAxleWeights) {
      updatedRow.axleWeights = [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
        ...DEFAULT_AXLE_DATA,
        axleNumber: index + 1,
      }))
    }
    apiRef.current.updateRows([updatedRow])
    onUpdateMaterial()
    // Wait for the grid to render with the new row
    setTimeout(() => {
      const newNumberOfRows = apiRef.current.getRowsCount()
      const pageSize = show20MaterialsOnIBTs ? 20 : 10
      apiRef.current.setPage(Math.ceil(newNumberOfRows / pageSize))
      apiRef.current.scrollToIndexes({
        rowIndex: newNumberOfRows - 1,
      })
    }, 150)
  }, [apiRef, enableAxleWeights, defaultNumberOfAxles])

  const onFirstRowAdd = useCallback(() => {
    if (!isUpdate && apiRef?.current && apiRef?.current?.getRowsCount?.() === 0) {
      setTimeout(() => {
        const id = randomId()
        const updatedRow = {
          id,
          rowType: IBT_ROW_TYPES.MATERIAL,
          isNew: true,
          rowIndex: 1,
          gross: 0,
          tare: null,
        }
        if (enableAxleWeights) {
          updatedRow.axleWeights = [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
            ...DEFAULT_AXLE_DATA,
            axleNumber: index + 1,
          }))
        }
        apiRef.current?.updateRows([updatedRow])

        apiRef.current?.scrollToIndexes({
          rowIndex: apiRef.current.getRowsCount() - 1,
        })
      }, 200)
    }
  }, [apiRef, isUpdate, enableAxleWeights, defaultNumberOfAxles])

  const onSaveDeduction = useCallback(
    deductions => {
      setOpenDeduction(false)

      const row = apiRef.current.getRow(deductionMaterialId)

      handleChange(null, { ...row, deductions })

      onUpdateMaterial()
    },
    [deductionMaterialId],
  )

  const styleProps = {
    '.MuiDataGrid-cell': {}, // remove !important paddingLeft styling
  }

  const fieldsNotScrapVehicles = ['iot-buttons-gross', 'iot-buttons-tare', 'actions']

  return (
    <Box p={3} data-testid="materials-list">
      <Typography variant="h6" gutterBottom color="textSecondary">
        Materials {materials.length ? `(${materials.length})` : null}
      </Typography>
      <BasicDataGrid
        className={classes.root}
        rows={materials}
        columns={materialColumns}
        getRowClassName={getRowClassName}
        onCellClick={(params, event) => {
          event.stopPropagation()

          apiRef.current.state.focus.cell = null
          totalApiRef.current.state.focus.cell = null

          // We don't want to open the modal if the user clicks on any other button
          if (params.row.rowType === IBT_ROW_TYPES.SCRAP_VEHICLE && !fieldsNotScrapVehicles.includes(params.field)) {
            onEditScrapVehicle(params.row.vin)
          }
        }}
        onRowEditCommit={() => onUpdateMaterial()}
        apiRef={apiRef}
        editMode="row"
        disableColumnMenu
        components={{
          LoadingOverlay: () => null,
        }}
        onMounted={onFirstRowAdd}
        styleProps={styleProps}
        pageSize={show20MaterialsOnIBTs ? 20 : 10}
        pagination
      />
      {!disabled && (
        <Box>
          <Button
            startIcon={<AddIcon />}
            onClick={onClickNewMaterial}
            data-testid="active-material-new-button"
            sx={{
              width: '100%',
            }}
          >
            Add New Material
          </Button>
        </Box>
      )}
      <BasicDataGrid
        dataTestId="load-total-grids"
        className={classes.root}
        rows={totalInformation}
        columns={materialColumns}
        onCellClick={(params, event) => {
          event.stopPropagation()

          apiRef.current.state.focus.cell = null
          totalApiRef.current.state.focus.cell = null

          if (!disabled && [IBT_ROW_TYPES.FEE, IBT_ROW_TYPES.PROMOTION].includes(params.row.rowType)) {
            setActiveFeeOrPromotionRow(params.row)
          }

          if (params.id === 'differential') {
            onClickDifferentialRow()
          }
        }}
        headerHeight={0}
        hideFooter
        getRowClassName={params => {
          const { id, rowType } = params.row
          if (id === 'load-total') return 'total-row'
          if (id === 'differential' && isDifferentialExceeded) {
            return 'differential-row'
          }
          if (!disabled && [IBT_ROW_TYPES.FEE, IBT_ROW_TYPES.PROMOTION].includes(rowType)) {
            return 'f-and-p-clickable-row'
          }
          return 'normal-row'
        }}
        apiRef={totalApiRef}
        styleProps={styleProps}
      />
      <AdjustmentModal
        title="Add a Material Level Adjustment"
        adjustments={selectedDeductions}
        open={openDeduction}
        onSave={onSaveDeduction}
        onClose={() => setOpenDeduction(false)}
        pricePrepend={pricePrepend}
      />
      <ScalePopover
        scalePopoverData={scalePopoverData}
        setScalePopoverData={setScalePopoverData}
        callReadScale={callReadScale}
        onScaleSelected={scaleId => {
          const { material, weightType } = scalePopoverData

          // Create array of filenames with deviceIds for the selected scale
          const fileNamesArray = scaleCameras[scaleId]?.map(cameraId => ({
            imageFilename: `${uuidv4()}-${weightType}.jpg`,
            deviceId: cameraId,
          }))

          // Request images with the generated filenames
          requestImages(
            imageData => {
              if (imageData.length > 0) {
                const fieldName = `materialGrid.${material.id}.${weightType}Images`
                setValue(fieldName, imageData)
                const ref = material.id === 'truck-total' ? totalApiRef : apiRef
                ref.current.updateRows([
                  {
                    id: material.id,
                    [`${weightType}Images`]: imageData,
                  },
                ])
              }
            },
            fileNamesArray,
            scaleId,
          )
        }}
      />
      <MultiImageBlade
        multiImageBladeData={multiImageBladeData}
        setMultiImageBladeData={setMultiImageBladeData}
        generateSaveImagesFunction={imageType => imageData => {
          const ref = multiImageBladeData.rowType === IBT_ROW_TYPES.TRUCK_TOTAL ? totalApiRef : apiRef
          const { axleNumber } = multiImageBladeData
          if (axleNumber) {
            const currentAxleWeightsArray = multiImageBladeData?.axleWeights || []
            const axleData = currentAxleWeightsArray?.find(axle => axle.axleNumber === axleNumber)
            const updatedAxleWeights = currentAxleWeightsArray.filter(axle => axle.axleNumber !== axleNumber)
            updatedAxleWeights.push({
              ...axleData,
              [`${imageType}Images`]: imageData,
            })
            if (axleNumber === 1) {
              ref.current.updateRows([
                { id: multiImageBladeData.id, axleWeights: updatedAxleWeights, [`${imageType}Images`]: imageData },
              ])
              setValue(`materialGrid.${multiImageBladeData.id}.${imageType}Images`, imageData)
            } else {
              ref.current.updateRows([{ id: multiImageBladeData.id, axleWeights: updatedAxleWeights }])
            }
            setMultiImageBladeData(prevMultiImageBladeData => ({
              ...prevMultiImageBladeData,
              axleWeights: updatedAxleWeights,
              [`${imageType}Images`]: imageData,
            }))
          } else {
            ref.current.updateRows([{ ...multiImageBladeData, [`${imageType}Images`]: imageData }])
            setValue(`materialGrid.${multiImageBladeData.id}.${imageType}Images`, imageData)
            setMultiImageBladeData(ref.current.getRow(multiImageBladeData.id))
          }
          setHasEditedMaterials(true)
        }}
        disabledUploadImages={disabled}
      />
      <AxleWeightsModal
        onClose={() => setActiveRowForAxleWeights(null)}
        open={!!activeRowForAxleWeights}
        materialData={activeRowForAxleWeights}
        onSave={(data, numberOfAxles) => {
          const gridApiRef = activeRowForAxleWeights?.id === 'truck-total' ? totalApiRef : apiRef

          if (numberOfAxles === 1) {
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.gross`, data.axles?.['axle-1']?.gross || 0, {
              shouldDirty: true,
            })
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.tare`, data.axles?.['axle-1']?.tare || 0)
            setValue(
              `materialGrid.${activeRowForAxleWeights?.id}.grossImages`,
              data.axles?.['axle-1']?.grossImages || [],
            )
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.tareImages`, data.axles?.['axle-1']?.tareImages || [])
            gridApiRef.current.updateRows([
              {
                id: activeRowForAxleWeights?.id,
                gross: data.axles?.['axle-1']?.gross || 0,
                tare: data.axles?.['axle-1']?.tare || 0,
                isManualGross: data.axles?.['axle-1']?.isManualGross || false,
                isManualTare: data.axles?.['axle-1']?.isManualTare || false,
                grossImages: data.axles?.['axle-1']?.grossImages || [],
                tareImages: data.axles?.['axle-1']?.tareImages || [],
                axleWeights: [],
              },
            ])
          } else {
            const oldAxleWeights = activeRowForAxleWeights?.axleWeights || []
            const {
              axleWeights: newAxleWeights,
              aggregateGross,
              aggregateTare,
              aggregateIsManualGross,
              aggregateIsManualTare,
            } = formatAxleWeightsFromFormData(data, numberOfAxles, oldAxleWeights)

            setValue(`materialGrid.${activeRowForAxleWeights?.id}.gross`, aggregateGross, { shouldDirty: true })
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.tare`, aggregateTare)
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.grossImages`, [])
            setValue(`materialGrid.${activeRowForAxleWeights?.id}.tareImages`, [])
            gridApiRef.current.updateRows([
              {
                id: activeRowForAxleWeights?.id,
                gross: aggregateGross,
                tare: aggregateTare,
                isManualGross: aggregateIsManualGross,
                isManualTare: aggregateIsManualTare,
                grossImages: data.axles?.['axle-1']?.grossImages || [],
                tareImages: data.axles?.['axle-1']?.tareImages || [],
                axleWeights: newAxleWeights,
              },
            ])
          }
        }}
        disabled={disabled}
      />
      <FeeOrPromotionModal
        open={!!activeFeeOrPromotionRow}
        onClose={() => setActiveFeeOrPromotionRow(null)}
        selectedFeeOrPromotionId={activeFeeOrPromotionRow?.id}
        onFeeOrPromotionUpdateSubmit={onFeeOrPromotionUpdateSubmit}
      />
      <AlertModal
        open={showDeleteModal}
        modalType="delete"
        title="Confirm Delete"
        question="Are you sure you want to delete this material?"
        description="All weights, images, and price changes for this material will be deleted."
        confirmLabel="Confirm Delete"
        cancelLabel="CANCEL"
        onClose={() => setShowDeleteModal(false)}
        onSubmit={() => handleDeleteClick(selectedIdForDeletion)}
      />
      <PurchasePriceHistoryBlade
        customerMaterialPair={customerMaterialPair}
        openCondition={openPurchasePriceHistoryBlade}
        onCloseFunction={() => setOpenPurchasePriceHistoryBlade(false)}
        useBackendSortAndFilter
      />
    </Box>
  )
}

ActiveMaterialList.propTypes = {
  materials: PropTypes.array,
  totalInformation: PropTypes.array,
  onUpdateMaterial: PropTypes.func.isRequired,
  isUpdate: PropTypes.bool,
  apiRef: PropTypes.object.isRequired,
  totalApiRef: PropTypes.object.isRequired,
  formApi: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onClickDifferentialRow: PropTypes.func,
  setDifferential: PropTypes.func,
  hasEditedMaterials: PropTypes.bool,
  setHasEditedMaterials: PropTypes.func,
  onEditScrapVehicle: PropTypes.func.isRequired,
  pricePrepend: PropTypes.string,
  setErrorMessage: PropTypes.func,
  purchaseOrderData: PropTypes.object,
  isTransfer: PropTypes.bool,
  isDirty: PropTypes.bool,
  onFeeOrPromotionUpdateSubmit: PropTypes.func,
}

ActiveMaterialList.defaultProps = {
  isUpdate: false,
  materials: [],
  totalInformation: [],
  disabled: false,
  onClickDifferentialRow: () => {},
  setDifferential: () => {},
  hasEditedMaterials: false,
  setHasEditedMaterials: () => {},
  pricePrepend: '$',
  setErrorMessage: () => {},
  purchaseOrderData: null,
  isTransfer: false,
  isDirty: false,
}

export default ActiveMaterialList
